import { AxiosInstance } from "axios";
import { SpecificDatePayload } from "../types/common/common-payload";
import { IdDto } from "../types/id/id-dto";
import { PreviewDto } from "../types/schedule/preview-dto";
import { ScheduleDto, SchedulePageDto } from "../types/schedule/schedule-dto";
import {
  AssignSchedulePayload,
  CreateSchedulePayload,
  DuplicateSchedulePayload,
  PreviewSchedulePayload,
  SearchSchedulePageByPeriodPayload,
  UpdateAliasPayload,
  UpdateSchedulePayload,
} from "../types/schedule/schedule-payload";
import { ongBaseApi } from "./ong-base-api";
import { OngBaseResponse } from "./ong-base-response";
import { toast } from "react-toastify";
import { store } from "../app/store";
import { endGlobalLoadingAction, startGlobalLoadingAction } from "../app/reducers/loading-slice";

export enum ScheduleApiUrl {
  Base = "/schedule",
  Create = "/schedule",
  Update = "/schedule",
  Delete = "/schedule",
  DeleteAll = "/schedules",
  GetById = "/v3/schedule",
  Preview = "/v2/schedule/preview",
  Search = "/v3/schedules",
  SearchAll = "/v3/schedules/all",
  Assign = "/assign",
  Alias = "/alias",
  Duplicate = "/v3/schedule/duplicate",
}

const withLoadingAndToast = async <T>(
  apiCall: () => Promise<T>,
  successMessage?: string
): Promise<T> => {
  try {
    store.dispatch(startGlobalLoadingAction());
    const result = await apiCall();
    if (successMessage) {
      toast.success(successMessage);
    }
    return result;
  } finally {
    store.dispatch(endGlobalLoadingAction());
  }
};

class ScheduleApi {
  private _api: AxiosInstance;

  constructor(api: AxiosInstance) {
    this._api = api;
  }

  async create(
    payload: CreateSchedulePayload
  ): Promise<OngBaseResponse<IdDto>> {
    return await this._api.post(ScheduleApiUrl.Create, payload);
  }

  async update(
    id: string,
    payload: UpdateSchedulePayload
  ): Promise<OngBaseResponse<null>> {
    return await this._api.patch(`${ScheduleApiUrl.Update}/${id}`, payload);
  }

  async delete(id: string): Promise<OngBaseResponse<null>> {
    return await this._api.delete(`${ScheduleApiUrl.Delete}/${id}`);
  }

  async deleteAll(specificDate: string): Promise<OngBaseResponse<null>> {
    return await this._api.delete(
      `${ScheduleApiUrl.DeleteAll}/${specificDate}`
    );
  }

  async getById(id: string): Promise<OngBaseResponse<ScheduleDto>> {
    return await this._api.get(`${ScheduleApiUrl.GetById}/${id}`);
  }

  async preview(
    payload: PreviewSchedulePayload
  ): Promise<OngBaseResponse<PreviewDto>> {
    return await this._api.post(ScheduleApiUrl.Preview, payload);
  }

  async search(
    payload: SearchSchedulePageByPeriodPayload
  ): Promise<OngBaseResponse<SchedulePageDto>> {
    return await this._api.get(ScheduleApiUrl.Search, {
      params: payload,
    });
  }

  async searchAll(
    payload: SpecificDatePayload
  ): Promise<OngBaseResponse<SchedulePageDto>> {
    return await this._api.get(ScheduleApiUrl.SearchAll, { params: payload });
  }

  async assign(
    id: string,
    payload: AssignSchedulePayload
  ): Promise<OngBaseResponse<null>> {
    return await this._api.patch(
      `${ScheduleApiUrl.Base}/${id}${ScheduleApiUrl.Assign}`,
      null,
      { params: payload }
    );
  }

  async updateAlias(
    id: string,
    payload: UpdateAliasPayload
  ): Promise<OngBaseResponse<null>> {
    return await this._api.patch(
      `${ScheduleApiUrl.Base}/${id}${ScheduleApiUrl.Alias}`,
      null,
      { params: payload }
    );
  }

  async duplicate(
    payload: DuplicateSchedulePayload
  ): Promise<OngBaseResponse<null>> {
    return await withLoadingAndToast(
      () => this._api.post(`${ScheduleApiUrl.Duplicate}`, payload),
      "스케줄이 복사되었습니다."
    );
  }
}

export const scheduleApi = new ScheduleApi(ongBaseApi);
